import React from 'react';
import { Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { FaSeedling, FaRocket, FaBriefcase, FaUsers, FaArrowRight, FaCheckCircle, FaComments } from 'react-icons/fa';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-20px);
  }
`;

const LandingContainer = styled.div`
  min-height: calc(100vh - 200px);
  background-color: ${props => props.theme.colors.background};
  animation: ${fadeIn} 0.6s ease-out;
`;

const HeroSection = styled.section`
  position: relative;
  background: linear-gradient(135deg, ${props => props.theme.colors.primary} 0%, ${props => props.theme.colors.primaryDark} 100%);
  padding: 6rem 2rem;
  text-align: center;
  color: white;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 20% 50%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
                radial-gradient(circle at 80% 80%, rgba(255, 255, 255, 0.1) 0%, transparent 50%);
    pointer-events: none;
  }
  
  @media (max-width: 768px) {
    padding: 4rem 1.5rem;
  }
`;

const HeroTitle = styled.h1`
  position: relative;
  font-size: 3.5rem;
  font-weight: 800;
  margin: 0 0 1.5rem 0;
  line-height: 1.1;
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
  letter-spacing: -0.5px;
  text-shadow: 0 2px 20px rgba(0, 0, 0, 0.2);
  
  @media (max-width: 1024px) {
    font-size: 2.8rem;
  }
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const HeroSubtitle = styled.p`
  position: relative;
  font-size: 1.3rem;
  margin: 0 0 3rem 0;
  opacity: 0.95;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.7;
  font-weight: 300;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
    margin-bottom: 2rem;
  }
`;

const HeroIcon = styled.div`
  position: relative;
  font-size: 6rem;
  margin: 0 0 3rem 0;
  display: flex;
  justify-content: center;
  align-items: center;
  animation: ${float} 6s ease-in-out infinite;
  
  svg {
    color: rgba(255, 255, 255, 0.95);
    filter: drop-shadow(0 4px 20px rgba(0, 0, 0, 0.3));
  }
  
  @media (max-width: 768px) {
    font-size: 4.5rem;
    margin: 0 0 2rem 0;
  }
`;

const HeroCTA = styled.div`
  position: relative;
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
`;

const CTAButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem 2rem;
  background-color: white;
  color: ${props => props.theme.colors.primary};
  text-decoration: none;
  font-weight: 600;
  font-size: 1.1rem;
  border-radius: 50px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 30px rgba(0, 0, 0, 0.3);
  }
  
  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 0.9rem 1.5rem;
  }
`;

const ContentSection = styled.section`
  max-width: 1200px;
  margin: 0 auto;
  padding: 5rem 2rem;
  
  @media (max-width: 768px) {
    padding: 3rem 1.5rem;
  }
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  color: ${props => props.theme.colors.text};
  margin: 0 0 1rem 0;
  text-align: center;
  font-weight: 700;
  letter-spacing: -0.5px;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.2rem;
  color: ${props => props.theme.colors.textSecondary};
  margin: 0 0 3rem 0;
  text-align: center;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 2rem;
  }
`;

const OptionsList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(450px, 1fr));
  gap: 2.5rem;
  margin-bottom: 4rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const OptionCard = styled.div`
  position: relative;
  background-color: ${props => props.theme.colors.surface};
  border: 2px solid ${props => props.theme.colors.border};
  border-radius: 16px;
  padding: 2.5rem;
  box-shadow: 0 4px 16px ${props => props.theme.colors.shadow};
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 4px;
    background: linear-gradient(90deg, ${props => props.theme.colors.primary} 0%, ${props => props.theme.colors.primaryLight} 100%);
    transform: scaleX(0);
    transition: transform 0.4s ease;
  }
  
  &:hover {
    box-shadow: 0 12px 40px ${props => props.theme.colors.shadowHover};
    transform: translateY(-8px);
    border-color: ${props => props.theme.colors.primary};
    
    &::before {
      transform: scaleX(1);
    }
  }
  
  @media (max-width: 768px) {
    padding: 2rem;
  }
`;

const OptionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
`;

const OptionIcon = styled.div`
  background: linear-gradient(135deg, ${props => props.theme.colors.primary} 0%, ${props => props.theme.colors.primaryDark} 100%);
  color: white;
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  flex-shrink: 0;
  box-shadow: 0 4px 12px ${props => props.theme.colors.shadowHover};
`;

const OptionTitle = styled.h3`
  font-size: 1.5rem;
  color: ${props => props.theme.colors.text};
  margin: 0;
  font-weight: 700;
  letter-spacing: -0.3px;
  
  @media (max-width: 768px) {
    font-size: 1.3rem;
  }
`;

const OptionDescription = styled.p`
  color: ${props => props.theme.colors.primary};
  margin: 0 0 0.75rem 0;
  line-height: 1.6;
  font-size: 1.1rem;
  font-weight: 600;
`;

const OptionSubtext = styled.p`
  color: ${props => props.theme.colors.textSecondary};
  margin: 0 0 1.5rem 0;
  line-height: 1.7;
  font-size: 1rem;
`;

const ActionsList = styled.div`
  display: grid;
  gap: 1rem;
  margin-top: 1.5rem;
`;

const ActionItem = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  background-color: ${props => props.theme.colors.surfaceAlt};
  border: 2px solid ${props => props.theme.colors.border};
  border-radius: 10px;
  color: ${props => props.theme.colors.text};
  text-decoration: none;
  font-weight: 500;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  &:hover {
    background-color: ${props => props.theme.colors.primary};
    color: white;
    border-color: ${props => props.theme.colors.primary};
    transform: translateX(8px);
    box-shadow: 0 4px 12px ${props => props.theme.colors.shadowHover};
  }
  
  svg {
    flex-shrink: 0;
    font-size: 0.9rem;
  }
`;

const SideBySideSection = styled.section`
  padding: 5rem 2rem;
  margin: 0;
  
  @media (max-width: 768px) {
    padding: 3rem 1.5rem;
  }
`;

const SideBySideContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  margin-bottom: 3rem;
  
  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 3rem;
  }
`;

const InfoContent = styled.div`
  text-align: center;
`;

const WhyContent = styled.div`
  text-align: center;
`;

const UnifiedTitle = styled.h2`
  font-size: 2.5rem;
  color: ${props => props.theme.colors.text};
  margin: 0 0 2rem 0;
  font-weight: 700;
  letter-spacing: -0.5px;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const InfoText = styled.p`
  color: ${props => props.theme.colors.textSecondary};
  margin: 0.75rem 0;
  line-height: 1.9;
  font-size: 1.15rem;
  
  strong {
    color: ${props => props.theme.colors.text};
    font-weight: 600;
  }
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const WhyText = styled.p`
  color: ${props => props.theme.colors.textSecondary};
  margin: 1rem 0;
  line-height: 1.9;
  font-size: 1.2rem;
  
  strong {
    color: ${props => props.theme.colors.text};
    font-weight: 600;
  }
  
  @media (max-width: 768px) {
    font-size: 1.05rem;
  }
`;

const CalloutText = styled.p`
  color: ${props => props.theme.colors.primary};
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0;
  line-height: 1.6;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 1.3rem;
  }
`;

const StepsSection = styled.section`
  max-width: 1000px;
  margin: 0 auto;
  padding: 5rem 2rem 3rem;
  
  @media (max-width: 768px) {
    padding: 3rem 1.5rem 2rem;
  }
`;

const StepsList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 2rem;
  margin: 3rem 0;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const StepItem = styled.div`
  text-align: center;
  padding: 2rem 1rem;
  background-color: ${props => props.theme.colors.surface};
  border: 2px solid ${props => props.theme.colors.border};
  border-radius: 16px;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 8px 24px ${props => props.theme.colors.shadowHover};
    transform: translateY(-4px);
  }
`;

const StepNumber = styled.div`
  width: 3rem;
  height: 3rem;
  margin: 0 auto 1rem;
  background: linear-gradient(135deg, ${props => props.theme.colors.primary} 0%, ${props => props.theme.colors.primaryDark} 100%);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1.3rem;
  box-shadow: 0 4px 12px ${props => props.theme.colors.shadowHover};
`;

const StepText = styled.div`
  color: ${props => props.theme.colors.text};
  font-size: 1.1rem;
  font-weight: 600;
  
  strong {
    color: ${props => props.theme.colors.primary};
  }
`;

const FinalCTA = styled.div`
  text-align: center;
  max-width: 1000px;
  margin: 0 auto;
  padding: 3rem 2rem;
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}10 0%, ${props => props.theme.colors.primaryDark}10 100%);
  border-radius: 20px;
  border: 2px solid ${props => props.theme.colors.primary}30;
  
  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
  }
`;

const Landing = () => {
  return (
    <LandingContainer>
      <HeroSection>
        <HeroIcon>
          <FaSeedling />
        </HeroIcon>
        <HeroTitle>Hustling helps South Africans earn, build, and collaborate</HeroTitle>
        <HeroSubtitle>
          Whether you're starting something or just looking to make extra money, you'll find your people here.
        </HeroSubtitle>
        <HeroCTA>
          <CTAButton to="/find-hustle">
            <FaRocket /> Explore Hustles
          </CTAButton>
          <CTAButton to="/find-gig">
            <FaBriefcase /> Find Gigs
          </CTAButton>
        </HeroCTA>
      </HeroSection>

      <ContentSection>
        <SectionTitle>Two Ways to Use Hustling</SectionTitle>
        <SectionSubtitle>
          Whether you're building something long-term or need quick help, we've got you covered.
        </SectionSubtitle>
        
        <OptionsList>
          <OptionCard>
            <OptionHeader>
              <OptionIcon>
                <FaRocket />
              </OptionIcon>
              <OptionTitle>Hustles</OptionTitle>
            </OptionHeader>
            <OptionDescription>
              Build something together.
            </OptionDescription>
            <OptionSubtext>
              For startups, long-term projects, and business ideas. Find partners and skills to grow your vision into reality.
            </OptionSubtext>
            <ActionsList>
              <ActionItem to="/share-hustle">
                <FaArrowRight /> Post a Hustle to find partners
              </ActionItem>
              <ActionItem to="/find-hustle">
                <FaArrowRight /> Join a Hustle and grow with it
              </ActionItem>
            </ActionsList>
          </OptionCard>

          <OptionCard>
            <OptionHeader>
              <OptionIcon>
                <FaBriefcase />
              </OptionIcon>
              <OptionTitle>Gigs</OptionTitle>
            </OptionHeader>
            <OptionDescription>
              Quick money for quick help.
            </OptionDescription>
            <OptionSubtext>
              For once-off jobs, short-term work, and immediate needs. Get extra hands or earn cash fast.
            </OptionSubtext>
            <ActionsList>
              <ActionItem to="/share-gig">
                <FaArrowRight /> Post a Gig for temporary help
              </ActionItem>
              <ActionItem to="/find-gig">
                <FaArrowRight /> Find a Gig and earn extra cash
              </ActionItem>
            </ActionsList>
          </OptionCard>
        </OptionsList>
      </ContentSection>

      <StepsSection>
        <SectionTitle>How It Works</SectionTitle>
        <StepsList>
          <StepItem>
            <StepNumber>1</StepNumber>
            <StepText>Sign up</StepText>
          </StepItem>
          <StepItem>
            <StepNumber>2</StepNumber>
            <StepText>Choose <strong>Hustles</strong> or <strong>Gigs</strong></StepText>
          </StepItem>
          <StepItem>
            <StepNumber>3</StepNumber>
            <StepText>Post or browse</StepText>
          </StepItem>
          <StepItem>
            <StepNumber>4</StepNumber>
            <StepText>Chat & connect</StepText>
          </StepItem>
        </StepsList>
      </StepsSection>

      <SideBySideSection>
        <SideBySideContainer>
          <InfoContent>
            <UnifiedTitle>For People Just Browsing</UnifiedTitle>
            <InfoText>You don't have to post anything to start.</InfoText>
            <InfoText>Just browse, click, and see what feels right.</InfoText>
            <InfoText>If something interests you, start a chat.</InfoText>
            <InfoText><strong>It's free to try.</strong></InfoText>
          </InfoContent>

          <WhyContent>
            <UnifiedTitle>Why We Built Hustling</UnifiedTitle>
            <WhyText>South Africans are hustling harder than ever.</WhyText>
            <WhyText>Some want to <strong>start something</strong>.</WhyText>
            <WhyText>Some want to <strong>join something</strong>.</WhyText>
            <WhyText>Some just need <strong>extra income now</strong>.</WhyText>
          </WhyContent>
        </SideBySideContainer>
        
        <CalloutText>
          We built Hustling to connect all of you in one place.
        </CalloutText>
      </SideBySideSection>

      <FinalCTA>
        <SectionTitle style={{ fontSize: '2rem', marginBottom: '1rem' }}>
          Ready to get started?
        </SectionTitle>
        <InfoText style={{ fontSize: '1.2rem', marginBottom: '2rem' }}>
          Find the right people for your venture
        </InfoText>
        <HeroCTA>
          <CTAButton to="/register">
            <FaCheckCircle /> Join Hustling Today
          </CTAButton>
        </HeroCTA>
      </FinalCTA>
    </LandingContainer>
  );
};

export default Landing;
